import { Component, inject, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../../app.config';
import { FhirResourceTransformService } from '../../shared/fhir-resource-transform.service';
import { StateService } from '../../shared/state.service';
import { UtilityService } from '../../shared/utility.service';
import { ResourceRefreshService } from '../../shared/resource-refresh.service';
import { SuccessMessageComponent } from '../../shared/success-message/success-message.component';
import { Bundle, Medication, ChargeItemDefinition } from 'fhir/r4';

type ImportAction = 'add' | 'edit' | 'delete';

@Component({
  selector: 'app-medication-import',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatRadioModule,
    FormsModule
  ],
  templateUrl: './medication-import.component.html',
  styleUrls: ['./medication-import.component.scss']
})
export class MedicationImportComponent {
  private http = inject(HttpClient);
  private baseApiUrl = inject(backendEndPointToken);
  private terminologyServer = inject(terminologyCanonicalUrlBaseToken);
  private fhirTransformService = inject(FhirResourceTransformService);
  private stateService = inject(StateService);
  private utilityService = inject(UtilityService);
  private dialogRef = inject(MatDialogRef<MedicationImportComponent>);
  private snackBar = inject(MatSnackBar);
  private resourceRefresh = inject(ResourceRefreshService);

  selectedFile: File | null = null;
  action: ImportAction = 'add';
  isImporting = false;
  progress = 0;
  importResults: { success: any[], failure: any[] } = { success: [], failure: [] };

  private readonly csvHeaders = [
    'id', 'name', 'status', 'form', 'manufacturer',
    'amountNumeratorValue', 'amountNumeratorUnit', 'amountDenominatorValue', 'amountDenominatorUnit',
    'ingredient1_name', 'ingredient1_isActive', 'ingredient1_strengthNumeratorValue', 'ingredient1_strengthNumeratorUnit', 'ingredient1_strengthDenominatorValue', 'ingredient1_strengthDenominatorUnit',
    'lotNumber', 'expirationDate',
    'totalRemaining', 'lowStockThreshold', 'lowStockAlertsEnabled',
    'currency', 'sellingPrice', 'costPrice'
  ];

  downloadTemplate() {
    const csvContent = this.csvHeaders.join(',') + '\n' +
      ',Example Med,active,tablet,PharmaCorp,500,mg,1,tablet,Paracetamol,true,500,mg,1,tablet,LOT123,2026-12-31,100,20,true,NGN,1500,1000';
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `medication_import_template_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.csv')) {
      this.selectedFile = file;
    } else {
      this.snackBar.open('Please select a valid CSV file.', 'Close', { duration: 3000 });
      this.selectedFile = null;
    }
  }

  async startImport() {
    if (!this.selectedFile) return;

    this.isImporting = true;
    this.progress = 0;
    this.importResults = { success: [], failure: [] };

    try {
      const text = await this.selectedFile.text();
      const rows = this.parseCSV(text);
      
      if (rows.length === 0) {
        throw new Error('CSV file is empty or invalid.');
      }

      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        try {
          await this.processRow(row).toPromise();
          this.importResults.success.push(row);
        } catch (error) {
          console.error('Row processing failed', row, error);
          row['_error'] = error;
          this.importResults.failure.push(row);
        }
        this.progress = Math.round(((i + 1) / total) * 100);
      }

      this.snackBar.openFromComponent(SuccessMessageComponent, {
        data: { message: `Import completed: ${this.importResults.success.length} success, ${this.importResults.failure.length} failed.` },
        duration: 5000
      });

      if (this.importResults.success.length > 0) {
        this.resourceRefresh.notify('medication-inventory', { full: true });
      }

    } catch (error: any) {
      this.snackBar.open(`Import failed: ${error.message}`, 'Close', { duration: 5000 });
    } finally {
      this.isImporting = false;
    }
  }

  private parseCSV(text: string): any[] {
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim());
    const dataRows = lines.slice(1);

    return dataRows.map(line => {
      const values = this.parseCSVLine(line);
      const obj: any = {};
      headers.forEach((header, index) => {
        obj[header] = values[index]?.trim() || '';
      });
      return obj;
    });
  }

  private parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  }

  private processRow(row: any) {
    if (this.action === 'delete') {
      if (!row.id) throw new Error('ID is required for delete action');
      return this.http.delete(`${this.baseApiUrl}/Medication/${row.id}`);
    }

    const medicationResource = this.buildMedicationFromRow(row);
    const chargeItemResource = this.buildChargeItemFromRow(row, medicationResource);

    if (this.action === 'add') {
      return this.http.post(`${this.baseApiUrl}/Medication`, medicationResource).pipe(
        switchMap((medResp: any) => {
          this.stateService.persistOrgWideResource(medResp, 'saved');
          const medicationReference = {
            reference: `Medication/${medResp.id}`,
            display: medResp.code?.text || 'Unnamed Medication'
          };
          const chargeItemToPost = {
            ...chargeItemResource,
            instance: [medicationReference]
          };
          return this.http.post(`${this.baseApiUrl}/ChargeItemDefinition`, chargeItemToPost);
        }),
        catchError(err => { throw err; })
      );
    } else {
      // Edit
      if (!row.id) throw new Error('ID is required for edit action');
      medicationResource.id = row.id;
      return this.http.put(`${this.baseApiUrl}/Medication/${row.id}`, medicationResource).pipe(
        switchMap((medResp: any) => {
          this.stateService.persistOrgWideResource(medResp, 'saved');
          // Update or Create ChargeItemDefinition? 
          // For simplicity in import, we'll try to find existing CID or create new one linked to this med.
          // Better: just post a new one or put if we had CID ID. 
          // Since we don't have CID ID in CSV easily, we'll create a new one for now (backend handles many CID per med usually by taking latest).
          const medicationReference = { reference: `Medication/${row.id}`, display: medResp.code?.text || 'Unnamed Medication' };
          const chargeItemToPost = { ...chargeItemResource, instance: [medicationReference] };
          return this.http.post(`${this.baseApiUrl}/ChargeItemDefinition`, chargeItemToPost);
        })
      );
    }
  }

  private buildMedicationFromRow(row: any): any {
    const ingredients = [];
    if (row.ingredient1_name) {
      ingredients.push({
        itemCodeableConcept: this.stateService.buildCodeableConcept(row.ingredient1_name),
        isActive: row.ingredient1_isActive?.toLowerCase() === 'true',
        strength: {
          numerator: { value: parseFloat(row.ingredient1_strengthNumeratorValue) || 0, unit: row.ingredient1_strengthNumeratorUnit },
          denominator: { value: parseFloat(row.ingredient1_strengthDenominatorValue) || 1, unit: row.ingredient1_strengthDenominatorUnit }
        }
      });
    }

    const medication: any = {
      resourceType: 'Medication',
      status: row.status || 'active',
      code: this.stateService.buildCodeableConcept(row.name),
      form: this.stateService.buildCodeableConcept(row.form),
      manufacturer: this.utilityService.referenceFromDelimited(row.manufacturer),
      amount: {
        numerator: { value: parseFloat(row.amountNumeratorValue) || 0, unit: row.amountNumeratorUnit },
        denominator: { value: parseFloat(row.amountDenominatorValue) || 1, unit: row.amountDenominatorUnit }
      },
      ingredient: ingredients,
      batch: {
        lotNumber: row.lotNumber,
        expirationDate: row.expirationDate ? new Date(row.expirationDate).toISOString() : undefined
      },
      extension: []
    };

    // Inventory Extensions
    const extUrl = this.terminologyServer + '/StructureDefinition/';
    if (row.totalRemaining) {
      medication.extension.push({
        url: extUrl + 'totalRemaining',
        valueQuantity: { value: parseFloat(row.totalRemaining), unit: row.amountDenominatorUnit }
      });
    }
    if (row.lowStockThreshold) {
      medication.extension.push({
        url: extUrl + 'lowStockThreshold',
        valueQuantity: { value: parseFloat(row.lowStockThreshold), unit: row.amountDenominatorUnit }
      });
    }
    if (row.lowStockAlertsEnabled) {
      medication.extension.push({
        url: extUrl + 'lowStockAlertsEnabled',
        valueBoolean: row.lowStockAlertsEnabled?.toLowerCase() === 'true'
      });
    }

    return medication;
  }

  private buildChargeItemFromRow(row: any, medResource: any): any {
    const currency = row.currency || 'NGN';
    const components = [];

    if (row.sellingPrice) {
      components.push({
        type: 'base',
        amount: { value: parseFloat(row.sellingPrice), currency }
      });
    }

    const extension = [];
    if (row.costPrice) {
      extension.push({
        url: "http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost",
        extension: [
          { url: "type", valueCode: 'base' },
          { url: "code", valueCodeableConcept: { text: 'cost price component' } },
          { url: "amount", valueMoney: { value: parseFloat(row.costPrice), currency } }
        ]
      });
    }

    return {
      resourceType: 'ChargeItemDefinition',
      status: 'active',
      title: row.name + ' Price Definition',
      url: `https://elikita-server.daalitech.com/ChargeItemDefinition/medication-import-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      propertyGroup: [{ priceComponent: components }],
      extension: extension
    };
  }

  downloadFailedRows() {
    if (this.importResults.failure.length === 0) return;

    const headers = [...this.csvHeaders, '_error'];
    const csvContent = headers.join(',') + '\n' +
      this.importResults.failure.map(row => {
        return headers.map(h => {
          let val = row[h] || '';
          if (val && typeof val === 'object') val = JSON.stringify(val).replace(/"/g, '""');
          if (val.toString().includes(',') || val.toString().includes('"')) {
            return `"${val.toString().replace(/"/g, '""')}"`;
          }
          return val;
        }).join(',');
      }).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `medication_import_failed_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  }

  close() {
    this.dialogRef.close();
  }
}
